import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Autocomplete, Button, TextField, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import CheckIcon from "@mui/icons-material/Check";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar } from "@mui/material";
import DataStatusAlert from "../../../../Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import { useDispatch, useSelector } from "react-redux";
import { selectAllUsers, selectUserToken, setDisplayErrorSnackBar } from "../../../../../../features/userSlice";
import { BusinessInsightsEndPointLogic } from "../../../../../../logic/AssetManagement/BusinessInsights/BusinessInsightsEndPointLogic";
import store from "../../../../../../store";
import SimpleBackdrop from "../../../../../BackDrop/BackDrop";
import { useSearchParams } from "react-router-dom";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function AssignAdgroupPopup({ open, onClose }: Props) {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const allUsers = useSelector(selectAllUsers);
  const [assigneeName, setAssigneeName] = React.useState<any>(null);
  const [assigneeId, setAssigneeId] = React.useState<any>(null);
  const [comment, setComment] = React.useState<string>("");
  const taskDetails = useSelector((state: any) => state.businessInsights.taskDetails);
  const { selectedSearchSite } = useSelector((state: any) => state?.businessInsights);
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [searchParams,] = useSearchParams();
  const insightType = searchParams.get("insightType");

  const handleCloseAssignModal = () => { // Close the Assign modal and clear the assignee details
    onClose(); // Close modal if success
    setAssigneeId(null); // Clear assignee ID
    setAssigneeName(null); // Clear assignee name
    setComment(""); // Clear comment
  }

  const handleAssigneeChange = (value: any) => { // Handle change in assignee selection
    setAssigneeId(value?.id || null);
    setAssigneeName(value || null);
  }

  React.useEffect(() => {
    BusinessInsightsEndPointLogic.getAllUserList(oauthTokenHeader); // Fetch all users when the component mounts
  }, [oauthTokenHeader]);

  const assignBodyBusinessInsights = { // Prepare the body for the API request
    "shared_uid": taskDetails.shared_uid,
    "assignee": assigneeId,
  };

  const handleActions = async () => {
    if (!assigneeId || !comment) return; // Prevent action if assignee or comment is not set
    setLoading(true); // Set loading state to true
    try {
      if (oauthTokenHeader && assignBodyBusinessInsights) {
        await BusinessInsightsEndPointLogic.assignBussinessInsights(oauthTokenHeader, assignBodyBusinessInsights, taskDetails?.shared_uid); // Assign the business insight
        await BusinessInsightsEndPointLogic.postCommentBusinessInsightTask(oauthTokenHeader, { object_type: "tasks", comment }, taskDetails?.shared_uid); // Post the comment
        await BusinessInsightsEndPointLogic.getTaskDetails(oauthTokenHeader, taskDetails?.shared_uid); // Refresh task details
        await BusinessInsightsEndPointLogic.getAllBusinessInsights(selectedSearchSite, oauthTokenHeader, dispatch); // Refresh all business insights
      }
    } catch (error: any) {
      store.dispatch(setDisplayErrorSnackBar({ message: error?.parsedText || "Error while fetching data. Please try again later.", type: "error", }));
    } finally {
      setLoading(false); // Reset loading state
      handleCloseAssignModal(); // Close the modal
      store.dispatch(setDisplayErrorSnackBar({ message: "Business insights have been assigned to " + (assigneeName?.first_name + " " + assigneeName?.last_name), type: 'success' }));
      setComment(""); // Clear comment after action
      setAssigneeId(null); // Clear assignee ID after action
      setAssigneeName(null); // Clear assignee name after action
    }
  }

  React.useEffect(() => { // useEffect hook to reset fields when the modal opens
    if(open) {
      setAssigneeId(null); // Reset assignee ID when modal opens
      setAssigneeName(null); // Reset assignee name when modal opens
      setComment(""); // Reset comment when modal opens
    }
  }, [open]);

  return (
    <React.Fragment>
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <Dialog open={open} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" maxWidth="md" fullWidth>
        <DialogTitle id="alert-dialog-title">{"Assign Insight"}</DialogTitle>
        <DialogContent>
          <TableContainer sx={{ marginBottom: "16px" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ padding: "6px 0px" }}>
                    <Typography fontWeight={500}>Task Name</Typography>
                  </TableCell>
                  <TableCell sx={{ padding: "6px 0px" }}>
                    <Typography fontWeight={500}>Assignee</Typography>
                  </TableCell>
                  <TableCell sx={{ padding: "6px 0px" }}>
                    <Typography fontWeight={500}>Status</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {taskDetails && (
                  <TableRow>
                    <TableCell sx={{ padding: "6px 0px" }}>
                      {taskDetails.data['query_output:sec_title'].value}
                    </TableCell>
                    <TableCell sx={{ padding: "6px 0px" }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          alt={taskDetails.assignee_fullname}
                          src=""
                          sx={{ width: 24, height: 24 }}
                        />
                        <Typography variant="body2">
                          {taskDetails.assignee_fullname}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell padding="none">
                      <DataStatusAlert status={taskDetails.current_status} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <DialogContentText id="alert-dialog-description">
            <Typography fontSize={14} fontWeight={400} color="#000000">
              Stakeholder
            </Typography>
            <Stack direction="row" gap={1} alignItems="flex-start" mt={2}>
              <Autocomplete
                value={assigneeName} // Ensure assignee is an object like { id: number, name: string }
                onChange={(_, newValue) => handleAssigneeChange(newValue)}
                getOptionLabel={(option) => option?.first_name + " " + option?.last_name}
                disablePortal={false}
                options={allUsers} // Use the full user objects as options
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assignee"
                    size="small"
                    placeholder="Choose Stakeholder from this list"
                    sx={{ input: { cursor: 'pointer' } }} // show pointer on hover
                    onKeyDown={(e) => e.preventDefault()} // Blocks manual typing
                    InputLabelProps={{
                      shrink: true, // Keeps label above the input field
                    }}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    required
                    color="secondary"
                    helperText="These fields are mandatory"
                  />
                )}
              />
              <TextField
                size="small"
                label="Reason"
                required
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                fullWidth
                placeholder="Explain why or how this task is resolved"
                color="secondary"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </Stack>
          </DialogContentText>
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button variant="text" color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              loading={loading}
              loadingPosition="end"
              disabled={!assigneeId || !comment || loading}
              data-tracker={insightType === "adgroup" ? "321" : "342"}
              endIcon={loading && <CheckIcon fontSize="small" />}
              onClick={() => handleActions()}
            >
              Assign
              <CheckIcon fontSize="small" />
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      )}
    </React.Fragment>
  );
}
